// src/features/branches/pages/BranchDetailPage.tsx
// PREMIUM REDESIGN: EXECUTIVE DASHBOARD, HORIZONTAL TABS, SINGLE/MULTI BUSINESS ENFORCEMENT

import { useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import * as settingsApi from '@/features/settings/services/settingsApi';
import * as bankingApi from '@/features/banking/services/bankingApi';
import * as businessApi from '@/features/business/services/businessApi';
import apiClient from '@/lib/api-client';
import type { BankAccount } from '@/features/banking/services/bankingApi';
import type { Business } from '@/features/business/services/businessApi';
import {
    ArrowLeft, Store, CreditCard, Users as UsersIcon, MapPin, Phone, Mail,
    Building2, Pencil, Plus, Trash2, Save, CheckCircle2, XCircle,
    TrendingUp, DollarSign, Activity, Star, Clock, Target, Award,
    ShoppingCart, PiggyBank, Landmark, Wallet, Eye, X, Sparkles,
    Layers, UserCheck, Search, Check, Shield, Lock, Copy, AlertTriangle,
    ChevronRight, ArrowUpRight, Link2, Unlink
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

// ============================================================
// TYPES
// ============================================================

interface Branch {
    id: string;
    name: string;
    code: string;
    businessId?: string;
    address: string | null;
    phone: string | null;
    email: string | null;
    isMainBranch: boolean;
    isDefault: boolean;
    isActive: boolean;
    businessName?: string;
    createdAt?: string;
}

interface StaffMember {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    roles: { id: string; name: string }[];
    totalSales?: string;
    transactionsCount?: number;
}

interface BranchPerformance {
    totalSales: string;
    totalTransactions: number;
    averageBasket: string;
    totalDiscounts: string;
    totalTax: string;
    activeShifts: number;
    voidCount: number;
    refundCount: number;
    topSellingDay: string;
    topSellingDayAmount: string;
    topStaff: { id: string; name: string; totalSales: string; transactions: number }[];
    paymentBreakdown: { method: string; total: string; count: number }[];
    dailySales: { date: string; total: string; transactions: number }[];
}

type TabType = 'overview' | 'staff' | 'accounts' | 'performance';

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0.00';
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const formatDate = (date: string) => {
    try {
        return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
        return 'N/A';
    }
};

const formatTimeAgo = (date: string) => {
    try {
        const now = new Date();
        const created = new Date(date);
        const diffMs = now.getTime() - created.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(date);
    } catch {
        return 'N/A';
    }
};

const accountTypeConfig: Record<string, { label: string; icon: React.ElementType; gradient: string; bgLight: string; border: string }> = {
    savings: { label: 'Savings', icon: PiggyBank, gradient: 'from-emerald-500 to-teal-600', bgLight: 'bg-emerald-50', border: 'border-emerald-200' },
    current: { label: 'Current', icon: Landmark, gradient: 'from-blue-500 to-indigo-600', bgLight: 'bg-blue-50', border: 'border-blue-200' },
    cash: { label: 'Cash', icon: Wallet, gradient: 'from-amber-500 to-orange-600', bgLight: 'bg-amber-50', border: 'border-amber-200' },
    other: { label: 'Other', icon: CreditCard, gradient: 'from-purple-500 to-violet-600', bgLight: 'bg-purple-50', border: 'border-purple-200' },
};

// ============================================================
// MODAL COMPONENTS
// ============================================================

function Modal({ open, onClose, title, icon: Icon, children }: {
    open: boolean; onClose: () => void; title: string; icon: React.ElementType; children: React.ReactNode;
}) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={onClose}>
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 10 }}
                        onClick={e => e.stopPropagation()}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-slate-100">
                        <div className="sticky top-0 bg-white rounded-t-3xl z-10">
                            <div className="px-7 py-5 bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 rounded-t-3xl text-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm shadow-inner">
                                            <Icon size={20} className="text-white" />
                                        </div>
                                        <h2 className="text-lg font-bold text-white tracking-tight">{title}</h2>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 rounded-xl bg-white/15 text-white hover:bg-white/30 transition-colors">
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="p-7">{children}</div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function DeleteModal({ target, onClose, onConfirm, isLoading }: {
    target: { type: string; id: string; name: string } | null;
    onClose: () => void; onConfirm: () => void; isLoading: boolean;
}) {
    return (
        <AnimatePresence>
            {target && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={onClose}>
                    <motion.div
                        initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                        onClick={e => e.stopPropagation()}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-7 text-center border border-slate-100">
                        <div className="w-16 h-16 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
                            <Trash2 size={28} className="text-rose-500" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">Delete {target.type}?</h3>
                        <p className="text-sm text-slate-500 mt-2">
                            This will permanently remove <strong>{target.name}</strong>. This action cannot be reversed.
                        </p>
                        <div className="flex gap-3 mt-7">
                            <Button variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
                            <Button variant="danger" onClick={onConfirm} isLoading={isLoading} className="flex-1">Delete</Button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ============================================================
// SKELETON
// ============================================================

function DetailSkeleton() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-6">
                <div className="h-6 w-36 bg-slate-200 rounded-lg animate-pulse" />
                <div className="rounded-3xl bg-slate-200 animate-pulse h-60" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse h-28" />
                    ))}
                </div>
            </div>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BranchDetailPage() {
    const { branchId } = useParams<{ branchId: string }>();
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);
    const tenant = useAuthStore(s => s.tenant);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    // Active Tab & Navigation
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab') as TabType;
    const [activeTab, setActiveTab] = useState<TabType>(tabParam || 'overview');

    // Modals
    const [showEditForm, setShowEditForm] = useState(false);
    const [showAccountForm, setShowAccountForm] = useState(false);
    const [showAssignAccountModal, setShowAssignAccountModal] = useState(false);
    const [assignModalSearch, setAssignModalSearch] = useState('');
    const [showNewBranchModal, setShowNewBranchModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
    const [copiedCode, setCopiedCode] = useState(false);

    // Forms
    const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});
    const [editForm, setEditForm] = useState({
        name: '', code: '', address: '', phone: '', email: '', isMainBranch: false
    });

    const [accountForm, setAccountForm] = useState({
        bankName: '', accountNumber: '', accountName: '', accountType: 'current',
        currency: 'NGN', openingBalance: '0', isDefault: false
    });

    const [newBranchForm, setNewBranchForm] = useState({
        name: '', code: '', address: '', phone: '', email: '', isMainBranch: false
    });
    const [newBranchErrors, setNewBranchErrors] = useState<Record<string, string>>({});

    const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string; name: string } | null>(null);

    // Staff modal state
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
    const [staffModalSearch, setStaffModalSearch] = useState('');
    const [staffFilterSearch, setStaffFilterSearch] = useState('');

    // ==================== ERROR PARSING ====================

    const extractErrorMessage = (err: any): string => {
        const apiError = err?.response?.data?.error;
        if (!apiError) return err?.message || 'An unexpected error occurred.';
        if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
            return apiError.details.map((d: any) => d.message).join('. ');
        }
        return apiError.message || 'An unexpected error occurred.';
    };

    const extractFieldErrors = (err: any): Record<string, string> => {
        const apiError = err?.response?.data?.error;
        if (!apiError?.details || !Array.isArray(apiError.details)) return {};
        const fieldErrors: Record<string, string> = {};
        apiError.details.forEach((detail: any) => {
            if (detail.field && detail.message) {
                fieldErrors[detail.field] = detail.message;
            }
        });
        return fieldErrors;
    };

    // ==================== QUERIES ====================

    const { data: branch, isLoading, isError, refetch } = useQuery<Branch>({
        queryKey: ['branch', branchId],
        queryFn: async () => {
            const branches = await settingsApi.getBranches(tenantId!);
            const list = Array.isArray(branches) ? branches : (branches as any)?.data || [];
            return list.find((b: Branch) => b.id === branchId) || null;
        },
        enabled: !!tenantId && !!branchId,
    });

    const { data: business } = useQuery<Business>({
        queryKey: ['business', branch?.businessId],
        queryFn: () => businessApi.getBusinessById(branch!.businessId!),
        enabled: !!branch?.businessId,
    });

    const { data: staffList } = useQuery<StaffMember[]>({
        queryKey: ['branch-staff', branchId],
        queryFn: async () => {
            const result = await settingsApi.getBranchStaff(tenantId!, branchId!);
            if (Array.isArray(result)) return result;
            if ((result as any)?.data && Array.isArray((result as any).data)) return (result as any).data;
            return [];
        },
        enabled: !!tenantId && !!branchId,
    });

    const { data: allStaff, isLoading: isLoadingAllStaff } = useQuery<StaffMember[]>({
        queryKey: ['staff', tenantId],
        queryFn: async () => {
            const result = await settingsApi.getStaff(tenantId!);
            if (Array.isArray(result)) return result;
            if (result?.data && Array.isArray(result.data)) return result.data;
            if (result?.users && Array.isArray(result.users)) return result.users;
            return [];
        },
        enabled: !!tenantId,
    });

    const { data: allBranchesList = [] } = useQuery<Branch[]>({
        queryKey: ['branches', tenantId],
        queryFn: async () => {
            const branches = await settingsApi.getBranches(tenantId!);
            return Array.isArray(branches) ? branches : (branches as any)?.data || [];
        },
        enabled: !!tenantId,
    });

    const { data: allTenantAccounts = [] } = useQuery<BankAccount[]>({
        queryKey: ['all-bank-accounts', tenantId],
        queryFn: () => bankingApi.getBankAccounts(),
        enabled: !!tenantId,
    });

    const accounts = useMemo(
        () => allTenantAccounts.filter(a => a.branchId === branchId),
        [allTenantAccounts, branchId]
    );

    const { data: performance } = useQuery<BranchPerformance>({
        queryKey: ['branch-performance', branchId],
        queryFn: async () => {
            try {
                const { data } = await apiClient.get(`/branches/${branchId}/performance`);
                return data?.data;
            } catch {
                return null;
            }
        },
        enabled: !!branchId,
    });

    // ==================== BUSINESS MODEL RULES ====================

    // A business is "Single" type if tenant.businessModel is 'single' OR business.type is 'single'
    const isSingleBusinessType =
        tenant?.businessModel === 'single' ||
        business?.type?.toLowerCase() === 'single';

    // Only Multi Business Types can create additional branches
    const canCreateBranch = !isSingleBusinessType;

    // ==================== MUTATIONS ====================

    const saveBranchMutation = useMutation({
        mutationFn: async () => {
            const cleanPayload = Object.fromEntries(
                Object.entries({
                    name: editForm.name.trim(),
                    code: editForm.code.trim().toUpperCase(),
                    address: editForm.address.trim() || undefined,
                    phone: editForm.phone.trim() || undefined,
                    email: editForm.email.trim() || undefined,
                    isMainBranch: editForm.isMainBranch,
                    businessId: branch?.businessId
                }).filter(([_, v]) => v !== undefined && v !== null && v !== '')
            );
            return settingsApi.updateBranch(tenantId!, branchId!, cleanPayload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branch', branchId] });
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            queryClient.invalidateQueries({ queryKey: ['business'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            addToast({ type: 'success', title: 'Branch Updated ✨', message: `${editForm.name} changes have been saved.` });
            setShowEditForm(false);
            setEditFormErrors({});
        },
        onError: (err: any) => {
            const fieldErrors = extractFieldErrors(err);
            if (Object.keys(fieldErrors).length > 0) setEditFormErrors(fieldErrors);
            addToast({ type: 'error', title: 'Update Failed', message: extractErrorMessage(err) });
        },
    });

    const createSiblingBranchMutation = useMutation({
        mutationFn: async () => {
            if (!canCreateBranch) {
                throw new Error('Single-type businesses are restricted to 1 branch. Upgrade to Multi-Business to add branches.');
            }
            const payload = {
                name: newBranchForm.name.trim(),
                code: newBranchForm.code.trim().toUpperCase(),
                businessId: branch!.businessId!,
                address: newBranchForm.address.trim() || undefined,
                phone: newBranchForm.phone.trim() || undefined,
                email: newBranchForm.email.trim() || undefined,
                isMainBranch: newBranchForm.isMainBranch,
            };
            return settingsApi.createBranch(tenantId!, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['business', branch?.businessId] });
            addToast({
                type: 'success',
                title: 'New Branch Created! 🏪',
                message: `${newBranchForm.name} was successfully registered for ${business?.name}.`
            });
            setShowNewBranchModal(false);
            setNewBranchForm({ name: '', code: '', address: '', phone: '', email: '', isMainBranch: false });
            setNewBranchErrors({});
        },
        onError: (err: any) => {
            const fe = extractFieldErrors(err);
            if (Object.keys(fe).length > 0) setNewBranchErrors(fe);
            addToast({ type: 'error', title: 'Branch Creation Failed', message: extractErrorMessage(err) });
        }
    });

    const saveAccountMutation = useMutation({
        mutationFn: async () => {
            const payload: any = {
                ...accountForm,
                branchId,
                openingBalance: parseFloat(accountForm.openingBalance) || 0,
                businessId: branch?.businessId
            };
            if (editingAccount) return bankingApi.updateBankAccount(editingAccount.id, payload);
            return bankingApi.createBankAccount(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branch-accounts', branchId] });
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['all-bank-accounts'] });
            addToast({ type: 'success', title: 'Account Saved', message: `Bank account details have been saved.` });
            setShowAccountForm(false);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Save Failed', message: extractErrorMessage(err) }),
    });

    const assignAccountMutation = useMutation({
        mutationFn: async ({ accountId, targetBranchId }: { accountId: string; targetBranchId: string | null | undefined }) => {
            return bankingApi.updateBankAccount(accountId, {
                branchId: targetBranchId || null,
                businessId: targetBranchId ? (branch?.businessId || undefined) : undefined,
            });
        },
        onSuccess: (_, vars) => {
            queryClient.invalidateQueries({ queryKey: ['all-bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['branch-accounts', branchId] });
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            addToast({
                type: 'success',
                title: vars.targetBranchId ? 'Account Assigned 🏦' : 'Account Unlinked',
                message: vars.targetBranchId
                    ? `Bank account assigned to ${branch?.name}.`
                    : `Bank account unlinked from ${branch?.name}.`,
            });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Action Failed', message: extractErrorMessage(err) }),
    });

    const filteredAssignAccounts = useMemo(() => {
        if (!assignModalSearch.trim()) return allTenantAccounts;
        const q = assignModalSearch.toLowerCase();
        return allTenantAccounts.filter(a =>
            a.bankName.toLowerCase().includes(q) ||
            a.accountNumber.toLowerCase().includes(q) ||
            a.accountName.toLowerCase().includes(q)
        );
    }, [allTenantAccounts, assignModalSearch]);

    const assignStaffMutation = useMutation({
        mutationFn: async (idsToAssign?: string[]) => {
            const ids = idsToAssign !== undefined ? idsToAssign : selectedStaffIds;
            return settingsApi.assignStaffToBranch(tenantId!, branchId!, ids);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['branch-staff', branchId] });
            queryClient.invalidateQueries({ queryKey: ['branches'] });
            queryClient.invalidateQueries({ queryKey: ['business'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['staff'] });
            const count = variables !== undefined ? variables.length : selectedStaffIds.length;
            addToast({
                type: 'success',
                title: 'Staff Assignment Updated',
                message: `${count} staff member(s) assigned to ${branch?.name}.`
            });
            setShowStaffModal(false);
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Assignment Failed', message: extractErrorMessage(err) });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: ({ type, id }: { type: string; id: string }) => {
            if (type === 'account') return bankingApi.deleteBankAccount(id);
            return Promise.resolve();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['branch-accounts', branchId] });
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            addToast({ type: 'success', title: 'Deleted', message: `${deleteConfirm?.name} has been removed.` });
            setDeleteConfirm(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: extractErrorMessage(err) }),
    });

    // ==================== HANDLERS ====================

    const handleCopyCode = () => {
        if (!branch?.code) return;
        navigator.clipboard.writeText(branch.code);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
        addToast({ type: 'info', title: 'Copied', message: `Branch code ${branch.code} copied to clipboard.` });
    };

    const handleEdit = () => {
        if (!branch) return;
        setEditForm({
            name: branch.name,
            code: branch.code,
            address: branch.address || '',
            phone: branch.phone || '',
            email: branch.email || '',
            isMainBranch: branch.isMainBranch
        });
        setEditFormErrors({});
        setShowEditForm(true);
    };

    const handleOpenStaffModal = () => {
        setSelectedStaffIds((staffList || []).map(s => s.id));
        setStaffModalSearch('');
        setShowStaffModal(true);
    };

    const toggleStaffSelection = (id: string) => {
        setSelectedStaffIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleRemoveStaff = (staffId: string) => {
        const remaining = (staffList || []).filter(s => s.id !== staffId).map(s => s.id);
        assignStaffMutation.mutate(remaining);
    };

    const handleEditAccount = (a: BankAccount) => {
        setEditingAccount(a);
        setAccountForm({
            bankName: a.bankName,
            accountNumber: a.accountNumber,
            accountName: a.accountName,
            accountType: a.accountType,
            currency: a.currency,
            openingBalance: a.openingBalance,
            isDefault: a.isDefault
        });
        setShowAccountForm(true);
    };

    // ==================== DERIVED STATS ====================

    const branchAccounts = accounts || [];
    const activeStaff = (staffList || []).filter(s => s.isActive);
    const totalBranchBalance = branchAccounts.reduce((s, a) => s + parseFloat(a.currentBalance || '0'), 0);
    const staffHealth = (staffList || []).length > 0 ? (activeStaff.length / (staffList || []).length) * 100 : 100;
    const accountsWithBalance = branchAccounts.filter(a => parseFloat(a.currentBalance || '0') > 0).length;

    const filteredAssignedStaff = (staffList || []).filter(s => {
        if (!staffFilterSearch) return true;
        const q = staffFilterSearch.toLowerCase();
        return `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
            s.email.toLowerCase().includes(q) ||
            s.roles?.some(r => r.name.toLowerCase().includes(q));
    });

    // ==================== RENDER CHECKS ====================

    if (isLoading) return <DetailSkeleton />;

    if (isError || !branch) return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <ErrorState
                title="Branch not found"
                message="The requested branch does not exist or you do not have permission to view it."
                onBack={() => navigate('/branches')}
            />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-6">

                {/* Back Button */}
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
                    <button
                        onClick={() => navigate(branch.businessId ? `/businesses/${branch.businessId}` : '/branches')}
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-emerald-700 group transition-colors">
                        <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                        Back to {business?.name || 'Branches'}
                    </button>
                </motion.div>

                {/* ========== EXECUTIVE HERO BANNER ========== */}
                <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br from-teal-700 via-emerald-600 to-teal-800 text-white">

                    {/* Ambient Glows */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.2),transparent_50%)]" />
                        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl" />
                    </div>

                    <div className="relative px-6 lg:px-10 py-8 lg:py-10">
                        {/* Top row */}
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">

                            {/* Left: Identity */}
                            <div className="flex items-start sm:items-center gap-5">
                                <motion.div
                                    whileHover={{ scale: 1.05, rotate: -2 }}
                                    className="w-20 h-20 lg:w-24 lg:h-24 rounded-3xl flex items-center justify-center bg-white/15 backdrop-blur-md ring-1 ring-white/30 shadow-2xl flex-shrink-0">
                                    <Store size={40} className="text-white" />
                                </motion.div>

                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2.5 flex-wrap">
                                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
                                            {branch.name}
                                        </h1>
                                        {branch.isMainBranch && (
                                            <Badge className="bg-amber-400/25 text-amber-200 border-amber-300/40 backdrop-blur-sm shadow-xs font-semibold">
                                                <Star size={11} className="mr-1 fill-amber-300 text-amber-300" /> Main Branch
                                            </Badge>
                                        )}
                                        <Badge className={clsx(
                                            'backdrop-blur-sm font-semibold border',
                                            branch.isActive
                                                ? 'bg-emerald-400/25 text-emerald-100 border-emerald-300/40'
                                                : 'bg-rose-500/25 text-rose-200 border-rose-300/40'
                                        )}>
                                            {branch.isActive ? 'Active' : 'Inactive'}
                                        </Badge>

                                        {/* Business Model Badge */}
                                        {isSingleBusinessType ? (
                                            <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 font-medium">
                                                <Lock size={10} className="mr-1 inline" /> Single Business Location
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-white/15 text-white/90 border-white/20 font-medium">
                                                <Layers size={10} className="mr-1 inline" /> Multi-Business Branch
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Business & Code link */}
                                    <div className="flex items-center gap-2 flex-wrap text-emerald-100/90 text-sm font-medium">
                                        {business && (
                                            <button
                                                onClick={() => navigate(`/businesses/${business.id}`)}
                                                className="inline-flex items-center gap-1 text-white hover:text-emerald-200 underline-offset-4 hover:underline transition-colors">
                                                <Building2 size={13} />
                                                <span>{business.name}</span>
                                            </button>
                                        )}
                                        <span className="text-white/40">•</span>
                                        <button
                                            onClick={handleCopyCode}
                                            title="Click to copy code"
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-mono transition-colors">
                                            <span>{branch.code}</span>
                                            {copiedCode ? <Check size={11} className="text-emerald-300" /> : <Copy size={11} className="text-white/70" />}
                                        </button>
                                    </div>

                                    {/* Contact info metadata */}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-white/70 text-xs">
                                        {branch.address && (
                                            <span className="flex items-center gap-1"><MapPin size={11} className="text-teal-200" /> {branch.address}</span>
                                        )}
                                        {branch.phone && (
                                            <span className="flex items-center gap-1"><Phone size={11} className="text-teal-200" /> {branch.phone}</span>
                                        )}
                                        {branch.email && (
                                            <span className="flex items-center gap-1"><Mail size={11} className="text-teal-200" /> {branch.email}</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Balance + Action Buttons */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto justify-between lg:justify-end border-t lg:border-t-0 border-white/15 pt-4 lg:pt-0">
                                <div className="text-left lg:text-right">
                                    <p className="text-white/60 text-[10px] uppercase tracking-widest font-bold">Branch Vault Balance</p>
                                    <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
                                        {formatNaira(totalBranchBalance)}
                                    </p>
                                    <p className="text-teal-100 text-xs">{branchAccounts.length} accounts configured</p>
                                </div>

                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        leftIcon={<Pencil size={15} />}
                                        onClick={handleEdit}
                                        className="!bg-white/20 !border-white/30 !text-white hover:!bg-white/30 !backdrop-blur-md !font-bold">
                                        Edit
                                    </Button>

                                    {/* Single vs Multi Business Branch Creation */}
                                    {canCreateBranch ? (
                                        <Button
                                            size="md"
                                            leftIcon={<Plus size={15} />}
                                            onClick={() => setShowNewBranchModal(true)}
                                            className="!bg-white !text-teal-800 hover:!bg-teal-50 !font-black !shadow-lg shadow-black/20">
                                            + New Branch
                                        </Button>
                                    ) : (
                                        <div
                                            title="Single business model: limited to 1 primary branch"
                                            className="px-3.5 py-2.5 rounded-xl bg-black/20 border border-white/15 text-white/70 text-xs font-semibold flex items-center gap-1.5 cursor-not-allowed">
                                            <Lock size={13} className="text-amber-300" />
                                            <span>Single Branch Limit</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Bottom metrics quick ribbon */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-8 pt-6 border-t border-white/15">
                            {[
                                { label: 'Assigned Staff', value: staffList?.length || 0, icon: UsersIcon },
                                { label: 'Active Roster', value: activeStaff.length, icon: CheckCircle2 },
                                { label: 'Bank Accounts', value: branchAccounts.length, icon: CreditCard },
                                { label: 'Health Status', value: `${Math.round(staffHealth)}%`, icon: Activity },
                                { label: 'Business Code', value: business?.shortCode || 'N/A', icon: Building2 },
                                { label: 'Created', value: formatTimeAgo(branch.createdAt || ''), icon: Clock },
                            ].map(item => (
                                <div key={item.label} className="bg-white/10 rounded-2xl p-3 backdrop-blur-sm border border-white/10">
                                    <div className="flex items-center gap-1.5 text-teal-200 text-[10px] uppercase font-bold tracking-wider mb-1">
                                        <item.icon size={12} />
                                        <span>{item.label}</span>
                                    </div>
                                    <p className="text-base font-extrabold text-white truncate">{item.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Notice banner if single business */}
                {isSingleBusinessType && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-900 flex items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-amber-100 text-amber-700 flex-shrink-0">
                                <Lock size={18} />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-amber-950">
                                    Single-Business Model: This is the dedicated primary branch
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    Single business accounts and businesses marked as "Single" type cannot add additional branches. Only Multi-Business accounts can create multiple branches.
                                </p>
                            </div>
                        </div>
                        <Badge variant="warning" size="sm" className="flex-shrink-0">
                            1 / 1 Branch
                        </Badge>
                    </motion.div>
                )}

                {/* ========== MODERN HORIZONTAL TAB BAR ========== */}
                <div className="flex items-center justify-between border-b border-slate-200 pb-1">
                    <div className="flex items-center gap-2 overflow-x-auto py-1">
                        {[
                            { key: 'overview' as const, label: 'Overview', icon: Activity, count: null },
                            { key: 'staff' as const, label: 'Assigned Staff', icon: UsersIcon, count: staffList?.length || 0 },
                            { key: 'accounts' as const, label: 'Bank Accounts', icon: CreditCard, count: branchAccounts.length },
                            { key: 'performance' as const, label: 'Sales & Activity', icon: TrendingUp, count: performance ? performance.totalTransactions : 0 },
                        ].map(tab => {
                            const isActive = activeTab === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={clsx(
                                        'relative flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap',
                                        isActive
                                            ? 'text-teal-700 bg-teal-50 border border-teal-200/80 shadow-xs'
                                            : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/70'
                                    )}>
                                    <tab.icon size={16} className={isActive ? 'text-teal-600' : 'text-slate-400'} />
                                    <span>{tab.label}</span>
                                    {tab.count !== null && (
                                        <span className={clsx(
                                            'px-2 py-0.5 rounded-full text-xs font-semibold',
                                            isActive
                                                ? 'bg-teal-600 text-white'
                                                : 'bg-slate-200 text-slate-600'
                                        )}>
                                            {tab.count}
                                        </span>
                                    )}
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTabIndicator"
                                            className="absolute bottom-0 left-2 right-2 h-0.5 bg-teal-600 rounded-full"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Quick tab context actions */}
                    {activeTab === 'staff' && (
                        <Button
                            size="sm"
                            leftIcon={<UsersIcon size={14} />}
                            onClick={handleOpenStaffModal}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            Manage Staff
                        </Button>
                    )}
                    {activeTab === 'accounts' && (
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<Link2 size={14} />}
                                onClick={() => {
                                    setAssignModalSearch('');
                                    setShowAssignAccountModal(true);
                                }}
                                className="border-teal-200 text-teal-800 hover:bg-teal-50 font-semibold">
                                Assign Existing Account
                            </Button>
                            <Button
                                size="sm"
                                leftIcon={<Plus size={14} />}
                                onClick={() => {
                                    setEditingAccount(null);
                                    setAccountForm({
                                        bankName: '', accountNumber: '', accountName: '',
                                        accountType: 'current', currency: 'NGN', openingBalance: '0', isDefault: false
                                    });
                                    setShowAccountForm(true);
                                }}
                                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                New Account
                            </Button>
                        </div>
                    )}
                </div>

                {/* ========== TAB CONTENT ========== */}
                <AnimatePresence mode="wait">

                    {/* ---------- OVERVIEW TAB ---------- */}
                    {activeTab === 'overview' && (
                        <motion.div
                            key="tab-overview"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-6">

                            {/* 3 Metric Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Team Deployment</p>
                                        <p className="text-3xl font-black text-slate-900 mt-1">
                                            {activeStaff.length} <span className="text-sm font-semibold text-slate-400">/ {staffList?.length || 0} active</span>
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">Authorized branch staff</p>
                                    </div>
                                    <div className="p-3.5 rounded-2xl bg-purple-50 text-purple-600">
                                        <UsersIcon size={26} />
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Vault & Accounts</p>
                                        <p className="text-3xl font-black text-emerald-600 mt-1">
                                            {formatNaira(totalBranchBalance)}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">{branchAccounts.length} accounts configured</p>
                                    </div>
                                    <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600">
                                        <Wallet size={26} />
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Sales Volume</p>
                                        <p className="text-3xl font-black text-teal-700 mt-1">
                                            {performance ? formatNaira(performance.totalSales) : '₦0.00'}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {performance ? `${performance.totalTransactions} transactions` : 'No transactions recorded'}
                                        </p>
                                    </div>
                                    <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600">
                                        <TrendingUp size={26} />
                                    </div>
                                </div>
                            </div>

                            {/* Branch Details & Settings Quick View */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 shadow-sm p-6 lg:p-8 space-y-6">
                                    <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                                        <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                                            <Store size={18} className="text-teal-600" />
                                            Branch Operational Details
                                        </h3>
                                        <Button size="sm" variant="secondary" onClick={handleEdit} leftIcon={<Pencil size={13} />}>
                                            Edit Details
                                        </Button>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Branch Name</p>
                                            <p className="text-base font-bold text-slate-900">{branch.name}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Branch Code</p>
                                            <code className="text-sm font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">
                                                {branch.code}
                                            </code>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Physical Address</p>
                                            <p className="text-sm font-medium text-slate-700">{branch.address || 'No address provided'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Contact Channels</p>
                                            <p className="text-sm font-medium text-slate-700">{branch.phone || 'No phone'} · {branch.email || 'No email'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Assigned Business</p>
                                            <p className="text-sm font-medium text-slate-700">{business?.name || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Designation</p>
                                            <p className="text-sm font-medium text-slate-700">
                                                {branch.isMainBranch ? 'Main Operating Branch' : 'Standard Branch Location'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Right card: Quick Actions & Status */}
                                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 lg:p-8 space-y-5">
                                    <h3 className="font-bold text-slate-900 text-lg">Quick Actions</h3>

                                    <div className="space-y-2.5">
                                        <button
                                            onClick={handleOpenStaffModal}
                                            className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-purple-50 text-purple-600 group-hover:bg-purple-100">
                                                    <UsersIcon size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-900">Manage Staff</p>
                                                    <p className="text-xs text-slate-400">{staffList?.length || 0} members assigned</p>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-slate-300 group-hover:text-teal-600 transition-colors" />
                                        </button>

                                        <button
                                            onClick={() => setActiveTab('accounts')}
                                            className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-100">
                                                    <CreditCard size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-900">Manage Bank Accounts</p>
                                                    <p className="text-xs text-slate-400">{branchAccounts.length} accounts configured</p>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-slate-300 group-hover:text-teal-600 transition-colors" />
                                        </button>

                                        {branch.businessId && (
                                            <button
                                                onClick={() => navigate(`/businesses/${branch.businessId}`)}
                                                className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left group">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100">
                                                        <Building2 size={16} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-slate-900">View Business</p>
                                                        <p className="text-xs text-slate-400">{business?.name || 'Parent entity'}</p>
                                                    </div>
                                                </div>
                                                <ChevronRight size={16} className="text-slate-300 group-hover:text-teal-600 transition-colors" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* ---------- STAFF TAB ---------- */}
                    {activeTab === 'staff' && (
                        <motion.div
                            key="tab-staff"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4">

                            {/* Staff Toolbar */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="relative w-full sm:w-80">
                                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search assigned staff by name or role..."
                                        value={staffFilterSearch}
                                        onChange={e => setStaffFilterSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                                    />
                                    {staffFilterSearch && (
                                        <button
                                            onClick={() => setStaffFilterSearch('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-400 font-medium">
                                        Showing {filteredAssignedStaff.length} of {staffList?.length || 0}
                                    </span>
                                    <Button
                                        size="sm"
                                        leftIcon={<UsersIcon size={14} />}
                                        onClick={handleOpenStaffModal}
                                        className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                        Manage & Assign
                                    </Button>
                                </div>
                            </div>

                            {/* Staff Cards Grid */}
                            {(staffList || []).length === 0 ? (
                                <EmptyState
                                    icon={<UsersIcon size={44} className="text-teal-600" />}
                                    title="No staff members assigned"
                                    description="Assign staff members to this branch so they can sign in, run POS terminals, and record sales transactions."
                                    action={{ label: 'Assign Staff to Branch', onClick: handleOpenStaffModal }}
                                />
                            ) : filteredAssignedStaff.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
                                    No staff member matches "{staffFilterSearch}".
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredAssignedStaff.map(staff => (
                                        <motion.div
                                            key={staff.id}
                                            whileHover={{ y: -2 }}
                                            className="bg-white rounded-2xl border border-slate-200 hover:border-teal-300 p-5 shadow-sm hover:shadow-md transition-all group flex flex-col justify-between">
                                            <div>
                                                <div className="flex items-start justify-between gap-3 mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-base shadow-sm">
                                                            {staff.firstName?.[0]}{staff.lastName?.[0]}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-slate-900 text-sm">
                                                                {staff.firstName} {staff.lastName}
                                                            </h4>
                                                            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                                                <Mail size={11} /> {staff.email}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <Badge variant={staff.isActive ? 'success' : 'danger'} size="sm">
                                                        {staff.isActive ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>

                                                {/* Roles */}
                                                <div className="flex flex-wrap gap-1.5 mt-2">
                                                    {(staff.roles || []).map(r => (
                                                        <span
                                                            key={r.id || r.name}
                                                            className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                                            {r.name}
                                                        </span>
                                                    ))}
                                                </div>

                                                {/* Sales stats if available */}
                                                {staff.totalSales && parseFloat(staff.totalSales) > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                                                        <span className="text-slate-400">Total Sales:</span>
                                                        <span className="font-bold text-emerald-600">{formatNaira(staff.totalSales)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveStaff(staff.id)}
                                                    className="text-xs text-rose-500 hover:text-rose-700 font-semibold px-2 py-1 rounded-lg hover:bg-rose-50 transition-colors">
                                                    Remove from branch
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* ---------- BANK ACCOUNTS TAB ---------- */}
                    {activeTab === 'accounts' && (
                        <motion.div
                            key="tab-accounts"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4">

                            {branchAccounts.length === 0 ? (
                                <div className="p-8 sm:p-12 text-center bg-white rounded-3xl border border-slate-200/80 shadow-xs max-w-lg mx-auto space-y-4">
                                    <div className="w-16 h-16 rounded-2xl bg-teal-50 border border-teal-100 text-teal-600 flex items-center justify-center mx-auto">
                                        <CreditCard size={32} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900">No bank accounts assigned to this branch</h3>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Assign an existing bank account from your organization or create a new dedicated account for this branch.
                                        </p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                                        <Button
                                            variant="secondary"
                                            leftIcon={<Link2 size={14} />}
                                            onClick={() => {
                                                setAssignModalSearch('');
                                                setShowAssignAccountModal(true);
                                            }}
                                            className="w-full sm:w-auto border-teal-200 text-teal-800 hover:bg-teal-50 font-semibold">
                                            Assign Existing Account
                                        </Button>
                                        <Button
                                            leftIcon={<Plus size={14} />}
                                            onClick={() => {
                                                setEditingAccount(null);
                                                setAccountForm({
                                                    bankName: '', accountNumber: '', accountName: '',
                                                    accountType: 'current', currency: 'NGN', openingBalance: '0', isDefault: false
                                                });
                                                setShowAccountForm(true);
                                            }}
                                            className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                            Create New Account
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {branchAccounts.map(account => {
                                        const cfg = accountTypeConfig[account.accountType] || accountTypeConfig.other;
                                        const Icon = cfg.icon;
                                        return (
                                            <motion.div
                                                key={account.id}
                                                whileHover={{ y: -3 }}
                                                className={clsx(
                                                    'p-6 rounded-3xl border-2 transition-all shadow-sm flex flex-col justify-between group',
                                                    cfg.bgLight,
                                                    cfg.border
                                                )}>
                                                <div>
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className={`p-3 rounded-2xl bg-gradient-to-br ${cfg.gradient} text-white shadow-md`}>
                                                            <Icon size={20} />
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1">
                                                            {account.isDefault && <Badge variant="success" size="sm">Default</Badge>}
                                                            <Badge variant={account.isActive ? 'success' : 'danger'} size="sm">
                                                                {account.isActive ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    <h4 className="font-extrabold text-slate-900 text-lg">{account.bankName}</h4>
                                                    <p className="text-xs text-slate-500 font-mono tracking-wider mt-0.5">{account.accountNumber}</p>
                                                    <p className="text-xs text-slate-400 mt-0.5">{account.accountName}</p>

                                                    <div className="mt-4 pt-3 border-t border-slate-200/60">
                                                        <p className="text-[10px] font-bold uppercase text-slate-400">Current Balance</p>
                                                        <p className="text-2xl font-black text-emerald-600 mt-0.5">
                                                            {formatNaira(account.currentBalance)}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between gap-2 mt-5 pt-3 border-t border-slate-200/60">
                                                    <Badge variant="neutral" size="sm">{cfg.label}</Badge>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => assignAccountMutation.mutate({ accountId: account.id, targetBranchId: null })}
                                                            className="text-xs font-semibold text-amber-600 hover:text-amber-800 hover:underline flex items-center gap-1"
                                                            title="Unlink from this branch (remains available in entity accounts)">
                                                            <Unlink size={11} /> Unlink
                                                        </button>
                                                        <span className="text-slate-300">•</span>
                                                        <button
                                                            onClick={() => handleEditAccount(account)}
                                                            className="text-xs font-semibold text-teal-600 hover:text-teal-800 hover:underline">
                                                            Edit
                                                        </button>
                                                        <span className="text-slate-300">•</span>
                                                        <button
                                                            onClick={() => setDeleteConfirm({ type: 'account', id: account.id, name: account.bankName })}
                                                            className="text-xs font-semibold text-rose-500 hover:text-rose-700 hover:underline">
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* ---------- PERFORMANCE TAB ---------- */}
                    {activeTab === 'performance' && (
                        <motion.div
                            key="tab-performance"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-6">

                            {!performance ? (
                                <EmptyState
                                    icon={<TrendingUp size={44} className="text-teal-600" />}
                                    title="No performance activity yet"
                                    description="Sales metrics, shift logs, and ticket analytics will automatically populate once transactions are recorded at this branch."
                                />
                            ) : (
                                <div className="space-y-6">
                                    {/* Stat Grid */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Sales</p>
                                            <p className="text-2xl font-black text-emerald-600 mt-1">{formatNaira(performance.totalSales)}</p>
                                        </div>
                                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Transactions</p>
                                            <p className="text-2xl font-black text-slate-900 mt-1">{performance.totalTransactions.toLocaleString()}</p>
                                        </div>
                                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Average Basket</p>
                                            <p className="text-2xl font-black text-teal-600 mt-1">{formatNaira(performance.averageBasket)}</p>
                                        </div>
                                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Active Shifts</p>
                                            <p className="text-2xl font-black text-slate-900 mt-1">{performance.activeShifts}</p>
                                        </div>
                                    </div>

                                    {/* Top staff at this branch */}
                                    {performance.topStaff && performance.topStaff.length > 0 && (
                                        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-4">
                                            <h4 className="font-bold text-slate-900 text-base flex items-center gap-2">
                                                <Sparkles size={16} className="text-amber-500" />
                                                Top Performing Staff at This Branch
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                {performance.topStaff.slice(0, 3).map((staff, i) => (
                                                    <div key={staff.id} className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center gap-3">
                                                        <div className={clsx(
                                                            'w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-xs',
                                                            i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : 'bg-amber-700'
                                                        )}>
                                                            #{i + 1}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="font-bold text-slate-900 text-sm truncate">{staff.name}</p>
                                                            <p className="text-xs text-slate-400">{staff.transactions} sales</p>
                                                            <p className="text-xs font-bold text-emerald-600 mt-0.5">{formatNaira(staff.totalSales)}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Payment Methods */}
                                    {performance.paymentBreakdown && performance.paymentBreakdown.length > 0 && (
                                        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm space-y-3">
                                            <h4 className="font-bold text-slate-900 text-base">Payment Method Breakdown</h4>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                {performance.paymentBreakdown.map(pm => (
                                                    <div key={pm.method} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                                                        <p className="text-xs font-bold text-slate-500 capitalize">{pm.method}</p>
                                                        <p className="text-lg font-black text-emerald-600 mt-0.5">{formatNaira(pm.total)}</p>
                                                        <p className="text-[10px] text-slate-400">{pm.count} transactions</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ========== MODALS ========== */}

                {/* Edit Branch Modal */}
                <Modal open={showEditForm} onClose={() => setShowEditForm(false)} title="Edit Branch Information" icon={Store}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Branch Name *"
                                value={editForm.name}
                                error={editFormErrors.name}
                                onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Main Branch"
                                required
                            />
                            <Input
                                label="Code *"
                                value={editForm.code}
                                error={editFormErrors.code}
                                onChange={e => setEditForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                placeholder="MB-01"
                                required
                            />
                        </div>

                        <Input
                            label="Physical Address"
                            value={editForm.address}
                            error={editFormErrors.address}
                            onChange={e => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Plot 12, Victoria Island"
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Phone Number"
                                value={editForm.phone}
                                error={editFormErrors.phone}
                                onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="+234..."
                            />
                            <Input
                                label="Email Address"
                                value={editForm.email}
                                error={editFormErrors.email}
                                onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="branch@domain.com"
                            />
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer p-3.5 bg-amber-50 rounded-2xl border border-amber-200">
                            <input
                                type="checkbox"
                                checked={editForm.isMainBranch}
                                onChange={e => setEditForm(prev => ({ ...prev, isMainBranch: e.target.checked }))}
                                className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                            />
                            <div>
                                <span className="text-sm font-bold text-amber-900">Mark as primary main branch</span>
                                <p className="text-xs text-amber-700">Only one primary branch is designated per business.</p>
                            </div>
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 mt-7">
                        <Button variant="secondary" onClick={() => setShowEditForm(false)}>Cancel</Button>
                        <Button
                            onClick={() => saveBranchMutation.mutate()}
                            isLoading={saveBranchMutation.isPending}
                            disabled={!editForm.name || !editForm.code}
                            leftIcon={<Save size={14} />}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            Save Changes
                        </Button>
                    </div>
                </Modal>

                {/* Create Sibling Branch Modal (Only for Multi Business Types) */}
                <Modal
                    open={showNewBranchModal}
                    onClose={() => setShowNewBranchModal(false)}
                    title={`Add Branch to ${business?.name || 'Business'}`}
                    icon={Store}>
                    <div className="space-y-4">
                        <div className="p-3 bg-teal-50 rounded-2xl border border-teal-200 text-teal-800 text-xs">
                            Creating branch under <strong>{business?.name}</strong>.
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Branch Name *"
                                value={newBranchForm.name}
                                error={newBranchErrors.name}
                                onChange={e => setNewBranchForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Ikeja Branch"
                                required
                            />
                            <Input
                                label="Branch Code *"
                                value={newBranchForm.code}
                                error={newBranchErrors.code}
                                onChange={e => setNewBranchForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                placeholder="e.g., IKJ-02"
                                required
                            />
                        </div>

                        <Input
                            label="Address"
                            value={newBranchForm.address}
                            error={newBranchErrors.address}
                            onChange={e => setNewBranchForm(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Street, City, State"
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Phone"
                                value={newBranchForm.phone}
                                error={newBranchErrors.phone}
                                onChange={e => setNewBranchForm(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="+234..."
                            />
                            <Input
                                label="Email"
                                value={newBranchForm.email}
                                error={newBranchErrors.email}
                                onChange={e => setNewBranchForm(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="branch@domain.com"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-7">
                        <Button variant="secondary" onClick={() => setShowNewBranchModal(false)}>Cancel</Button>
                        <Button
                            onClick={() => createSiblingBranchMutation.mutate()}
                            isLoading={createSiblingBranchMutation.isPending}
                            disabled={!newBranchForm.name || !newBranchForm.code}
                            leftIcon={<Plus size={14} />}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            Create Branch
                        </Button>
                    </div>
                </Modal>

                {/* Account Form Modal */}
                <Modal
                    open={showAccountForm}
                    onClose={() => setShowAccountForm(false)}
                    title={editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
                    icon={CreditCard}>
                    <div className="space-y-4">
                        <Input
                            label="Bank Name *"
                            value={accountForm.bankName}
                            onChange={e => setAccountForm(prev => ({ ...prev, bankName: e.target.value }))}
                            placeholder="e.g., Access Bank, GTBank"
                            required
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Account Number *"
                                value={accountForm.accountNumber}
                                onChange={e => setAccountForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                                placeholder="0123456789"
                                required
                            />
                            <Input
                                label="Account Name *"
                                value={accountForm.accountName}
                                onChange={e => setAccountForm(prev => ({ ...prev, accountName: e.target.value }))}
                                placeholder="Branch Operating Acct"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Account Type
                                </label>
                                <select
                                    value={accountForm.accountType}
                                    onChange={e => setAccountForm(prev => ({ ...prev, accountType: e.target.value }))}
                                    className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                                    <option value="current">Current</option>
                                    <option value="savings">Savings</option>
                                    <option value="cash">Cash Vault</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <Input
                                label="Opening Balance (₦)"
                                type="number"
                                value={accountForm.openingBalance}
                                onChange={e => setAccountForm(prev => ({ ...prev, openingBalance: e.target.value }))}
                                placeholder="0.00"
                            />
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer p-3 bg-blue-50 rounded-2xl border border-blue-200">
                            <input
                                type="checkbox"
                                checked={accountForm.isDefault}
                                onChange={e => setAccountForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                                className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                                <span className="text-sm font-bold text-blue-900">Set as default account</span>
                                <p className="text-xs text-blue-600">Transactions for this branch will default to this account.</p>
                            </div>
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 mt-7">
                        <Button variant="secondary" onClick={() => setShowAccountForm(false)}>Cancel</Button>
                        <Button
                            onClick={() => saveAccountMutation.mutate()}
                            isLoading={saveAccountMutation.isPending}
                            disabled={!accountForm.bankName || !accountForm.accountNumber}
                            leftIcon={<Save size={14} />}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                            {editingAccount ? 'Save Changes' : 'Create Account'}
                        </Button>
                    </div>
                </Modal>

                {/* Manage Staff Modal */}
                <AnimatePresence>
                    {showStaffModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowStaffModal(false)}>
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
                                <div className="px-7 py-5 bg-gradient-to-r from-teal-700 via-emerald-600 to-teal-800 text-white flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20">
                                            <UsersIcon size={20} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-white">Manage Branch Staff</h2>
                                            <p className="text-teal-100 text-xs">{branch?.name} ({branch?.code})</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowStaffModal(false)}
                                        className="p-2 rounded-xl bg-white/15 text-white hover:bg-white/30 transition-colors">
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-xs text-slate-500 font-medium">
                                            Select staff members authorized to operate at this branch ({selectedStaffIds.length} assigned)
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedStaffIds((allStaff || []).map(s => s.id))}
                                                className="text-[11px] font-semibold text-teal-700 hover:underline">
                                                Select All
                                            </button>
                                            <span className="text-slate-300">•</span>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedStaffIds([])}
                                                className="text-[11px] font-semibold text-slate-500 hover:underline">
                                                Clear
                                            </button>
                                        </div>
                                    </div>

                                    {/* Search input */}
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                        <input
                                            type="text"
                                            placeholder="Search by staff name, email, or role..."
                                            value={staffModalSearch}
                                            onChange={e => setStaffModalSearch(e.target.value)}
                                            className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                                        />
                                    </div>

                                    {/* Staff list */}
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                        {isLoadingAllStaff ? (
                                            <div className="py-8 text-center"><Spinner size="md" /></div>
                                        ) : (allStaff || []).filter(s => {
                                            if (!staffModalSearch) return true;
                                            const term = staffModalSearch.toLowerCase();
                                            return s.firstName?.toLowerCase().includes(term) ||
                                                s.lastName?.toLowerCase().includes(term) ||
                                                s.email?.toLowerCase().includes(term) ||
                                                s.roles?.some(r => r.name.toLowerCase().includes(term));
                                        }).length === 0 ? (
                                            <p className="text-center py-6 text-xs text-slate-400">No staff found matching search.</p>
                                        ) : (
                                            (allStaff || [])
                                                .filter(s => {
                                                    if (!staffModalSearch) return true;
                                                    const term = staffModalSearch.toLowerCase();
                                                    return s.firstName?.toLowerCase().includes(term) ||
                                                        s.lastName?.toLowerCase().includes(term) ||
                                                        s.email?.toLowerCase().includes(term) ||
                                                        s.roles?.some(r => r.name.toLowerCase().includes(term));
                                                })
                                                .map(staff => {
                                                    const isSelected = selectedStaffIds.includes(staff.id);
                                                    return (
                                                        <label
                                                            key={staff.id}
                                                            className={clsx(
                                                                'flex items-center gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all',
                                                                isSelected
                                                                    ? 'border-teal-500 bg-teal-50/70 shadow-xs'
                                                                    : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50/60'
                                                            )}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleStaffSelection(staff.id)}
                                                                className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                                            />
                                                            <div className="w-8 h-8 rounded-xl bg-teal-100 text-teal-800 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                                                {staff.firstName?.[0]}{staff.lastName?.[0]}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-xs font-bold text-slate-800 truncate">
                                                                        {staff.firstName} {staff.lastName}
                                                                    </p>
                                                                    {staff.roles?.map(r => (
                                                                        <span key={r.id || r.name} className="px-1.5 py-0.2 rounded text-[9px] font-medium bg-slate-200 text-slate-700">
                                                                            {r.name}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                                <p className="text-[11px] text-slate-400 truncate">{staff.email}</p>
                                                            </div>
                                                            <Badge variant={staff.isActive ? 'success' : 'danger'} size="sm">
                                                                {staff.isActive ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </label>
                                                    );
                                                })
                                        )}
                                    </div>
                                </div>

                                <div className="px-7 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                    <span className="text-xs text-slate-500 font-medium">{selectedStaffIds.length} staff selected</span>
                                    <div className="flex gap-2">
                                        <Button variant="secondary" size="sm" onClick={() => setShowStaffModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => assignStaffMutation.mutate(undefined)}
                                            isLoading={assignStaffMutation.isPending}
                                            leftIcon={<Save size={14} />}
                                            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                            Save Assignments
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                    {/* Assign Existing Account Modal */}
                    {showAssignAccountModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
                            <motion.div
                                initial={{ scale: 0.95, y: 15 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 15 }}
                                className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200">
                                {/* Header */}
                                <div className="px-6 py-5 bg-gradient-to-r from-teal-800 to-slate-900 text-white flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-bold flex items-center gap-2">
                                            <Link2 size={20} className="text-teal-300" />
                                            Assign Bank Accounts to {branch?.name}
                                        </h3>
                                        <p className="text-xs text-teal-100/80 mt-0.5">
                                            Link existing settlement accounts or petty cash vaults to this branch location.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowAssignAccountModal(false)}
                                        className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition">
                                        <X size={18} />
                                    </button>
                                </div>

                                {/* Content & Search */}
                                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                                    <div className="relative">
                                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                        <Input
                                            placeholder="Search by bank name, account number or account name..."
                                            value={assignModalSearch}
                                            onChange={(e) => setAssignModalSearch(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        {filteredAssignAccounts.length === 0 ? (
                                            <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                                <CreditCard className="mx-auto text-slate-400 mb-2" size={32} />
                                                <p className="text-sm font-bold text-slate-700">No bank accounts found</p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {allTenantAccounts.length === 0
                                                        ? "You don't have any created bank accounts yet. Click below to create one."
                                                        : "No accounts match your search filter."}
                                                </p>
                                            </div>
                                        ) : (
                                            filteredAssignAccounts.map((acc) => {
                                                const isAssignedToThisBranch = acc.branchId === branchId;
                                                const assignedOtherBranch = acc.branchId && acc.branchId !== branchId
                                                    ? allBranchesList.find(b => b.id === acc.branchId)
                                                    : null;
                                                const cfg = accountTypeConfig[acc.accountType] || accountTypeConfig.other;
                                                const Icon = cfg.icon;

                                                return (
                                                    <div
                                                        key={acc.id}
                                                        className={clsx(
                                                            "p-4 rounded-2xl border-2 transition-all flex items-center justify-between gap-4",
                                                            isAssignedToThisBranch ? "border-teal-500/80 bg-teal-50/50" : "border-slate-200 hover:border-slate-300 bg-white"
                                                        )}>
                                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                                            <div className={`p-2.5 rounded-xl bg-gradient-to-br ${cfg.gradient} text-white shadow-sm flex-shrink-0`}>
                                                                <Icon size={18} />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <h4 className="font-bold text-slate-900 text-sm">{acc.bankName}</h4>
                                                                    <Badge variant="neutral" size="sm">{cfg.label}</Badge>
                                                                    {isAssignedToThisBranch && (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-800">
                                                                            <Check size={10} /> Assigned to this Branch
                                                                        </span>
                                                                    )}
                                                                    {assignedOtherBranch && (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                                                                            <Store size={10} /> Assigned to: {assignedOtherBranch.name}
                                                                        </span>
                                                                    )}
                                                                    {!acc.branchId && (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                                                                            Unassigned / General Vault
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                                                    <span className="font-mono">{acc.accountNumber}</span>
                                                                    <span>•</span>
                                                                    <span className="truncate">{acc.accountName}</span>
                                                                    <span>•</span>
                                                                    <span className="font-bold text-emerald-600">{formatNaira(acc.currentBalance)}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Action button */}
                                                        <div className="flex-shrink-0">
                                                            {isAssignedToThisBranch ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    onClick={() => assignAccountMutation.mutate({ accountId: acc.id, targetBranchId: null })}
                                                                    isLoading={assignAccountMutation.isPending}
                                                                    className="border-amber-200 text-amber-700 hover:bg-amber-50 text-xs">
                                                                    <Unlink size={12} className="mr-1" /> Unlink
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => assignAccountMutation.mutate({ accountId: acc.id, targetBranchId: branchId || null })}
                                                                    isLoading={assignAccountMutation.isPending}
                                                                    className="bg-teal-600 hover:bg-teal-700 text-white text-xs">
                                                                    <Link2 size={12} className="mr-1" /> {assignedOtherBranch ? "Reassign Here" : "Assign to Branch"}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAssignAccountModal(false);
                                            setEditingAccount(null);
                                            setAccountForm({
                                                bankName: '', accountNumber: '', accountName: '',
                                                accountType: 'current', currency: 'NGN', openingBalance: '0', isDefault: false
                                            });
                                            setShowAccountForm(true);
                                        }}
                                        className="text-xs font-bold text-teal-700 hover:text-teal-900 hover:underline flex items-center gap-1">
                                        <Plus size={14} /> Need a different bank? Create New Bank Account
                                    </button>
                                    <Button variant="secondary" size="sm" onClick={() => setShowAssignAccountModal(false)}>
                                        Done
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Delete Modal */}
                <DeleteModal
                    target={deleteConfirm}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={() => deleteMutation.mutate({ type: deleteConfirm!.type, id: deleteConfirm!.id })}
                    isLoading={deleteMutation.isPending}
                />
            </div>
        </div>
    );
}